import AssessmentKSCharts from '@components/Charts/AssessmentKSCharts'
import {
  AssessmentColumnType as ColumnType,
  useAssessmentTable
} from '@suntex.fim/assessment'
import { Button } from '@components/ui'
import { useAssessmentDropdownFilters } from '@hooks/useAssessmentDropdownFilters'
import { useQuery } from '@tanstack/react-query'
import {
  GetAPIparams,
  renderTitle,
  useDropdownMenu
} from '@utils/assessmentReportUtils'
import { Flex } from 'antd'
import { useEffect, useMemo, useState } from 'react'

import { useLocation } from '@suntex.core/nav'
import { TeamId } from '@suntex.fim/api-client'
import {
  getAssessmentReportData,
  getValidAssessmentOption,
  schoolAssessmentInitialSorting,
  teamAssessmentInitialSorting
} from '@suntex.fim/assessment'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import { $EntityLevel, $KnowAndShowLevel, EntityLevel } from '@suntex.fim/enums'
import { useFramework, useRotateDevice } from '@suntex.fim/framework'
import { SendMessage } from '@suntex.fim/messages'
import { useAppQueryString } from '@suntex.fim/nav'
import { useOptionalTeamInfo } from '@suntex.fim/team'
import {
  ExportMenu,
  LevelSelect,
  LoaderOverlay,
  SearchInput
} from '@suntex.fim/ui'

import { appSessionStorage } from '../../utils/storageUtils'

import {
  ALL_COLUMN_KEYS,
  HIDABLE_SELECT_LABELS,
  KASReportData,
  SCHOOL_HIDABLE_SELECT_LABELS,
  columns,
  schoolColumns
} from './AssessmentReport/AssessmentReportKnowAndShowTable'
import AssessmentReportNavigation from './AssessmentReport/AssessmentReportNavigation'
import AssessmentReportSummaryDashboard from './AssessmentReport/AssessmentReportSummaryDashboard'
import FactFluency from './AssessmentReport/FactFluency'
import SelectedRowPopUp from './AssessmentReport/SelectedRowPopUp'

const levelOptions = $KnowAndShowLevel.listOptions()

/**
 * returns JSX for know and show report
 */
function AssessmentReportKnowAndShow() {
  const { api } = useFramework()
  useRotateDevice()
  const {
    kasLevel: queryKasLevel,
    grade: queryGrade,
    year: queryYear
  } = useAppQueryString()
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { state, pathname } = useLocation()
  const team = useOptionalTeamInfo()
  const { login } = useAuth(isAuthenticated)
  const isSchool =
    login.entityLevel === EntityLevel.School && pathname.includes('school')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [dropdownFilters, setDropDownFilters] = useAssessmentDropdownFilters()
  const [searchQuery, setSearchQuery] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const initiallevel: number = state?.level
    ? getValidAssessmentOption(
        levelOptions,
        state.level,
        levelOptions.at(-1)!.key
      )
    : levelOptions[0]!.key

  const selectedLevel =
    $KnowAndShowLevel.fromOptional(queryKasLevel) ||
    appSessionStorage.kasLevel ||
    initiallevel

  const [level, setLevel] = useState(() =>
    getValidAssessmentOption(levelOptions, selectedLevel, initiallevel)
  )
  const [sendMessageData, setSendMessageData] = useState<any[]>([])
  /**
   * open modal for send message and set sendMessageData state value
   */
  const sendMessage = (data: any[]) => {
    setIsModalOpen(true)
    setSendMessageData(data)
  }
  const memoColumns = useMemo<ColumnType<KASReportData>[]>(
    () =>
      isSchool
        ? schoolColumns({ level, graphColor: '#83E2B8' })
        : columns({ level, graphColor: '#83E2B8', sendMessage }),
    [level]
  )

  const { id, year, grade } = GetAPIparams(isSchool, dropdownFilters)
  const { data: summaryRecordData } = useQuery({
    queryKey: ['assessment', 'kasSummary', id, isSchool, year, grade],
    queryFn: async () => {
      const response =
        await api.get_Assessment_KnowAndShowPercentAggregateByLevels({
          id: id!,
          level: isSchool ? login.entityLevel : EntityLevel.Team,
          year: year!,
          aggregateLevel: login?.entityLevel,
          grade
        })
      return response.data[0] || {}
    },
    enabled: id != null && year != null
  })

  const { data: assessmentReport, isLoading } = useQuery({
    queryKey: ['assessment', 'kas', id, isSchool, year, grade],
    queryFn: async () => {
      if (isSchool && login.entityLevel) {
        const response =
          await api.get_Assessment_KnowAndShowPercentAggregateByLevels({
            id: id!,
            level: login.entityLevel,
            year: year!,
            aggregateLevel: $EntityLevel.from(login.entityLevel - 1),
            grade
          })
        return response.data
      } else {
        const response = await api.get_Assessment_KnowAndShowTeam({
          teamId: id as TeamId
        })
        return response.teamKnowAndShows || []
      }
    },
    enabled: id != null
  })
  const reportData = getAssessmentReportData(assessmentReport)

  // Update localStorage whenever level changes
  useEffect(() => {
    appSessionStorage.kasLevel = level
  }, [level, year, grade])

  /**
   * Initialize usetable with initial data and
   * Destructure table and Table from useTable hooks
   */
  const { table, Table } = useAssessmentTable<KASReportData>({
    data: reportData,
    columns: memoColumns,
    isLoading,
    globalFilter: searchQuery,
    horizontalScroll: true,
    hideFirstTwoColumns: !isSchool,
    hideFirstColumns: !isSchool,
    initialSorting: isSchool
      ? schoolAssessmentInitialSorting
      : teamAssessmentInitialSorting
  })
  const selectedRow = table.getSelectedRowModel().flatRows

  const LEVEL_TWO_THRESHOLD = 2
  const LEVEL_TWO_COLUMN_COUNT = 4
  const keys =
    level > LEVEL_TWO_THRESHOLD
      ? [...ALL_COLUMN_KEYS]
      : [...ALL_COLUMN_KEYS].splice(0, LEVEL_TWO_COLUMN_COUNT)

  const isDisabled = !selectedRow.length

  const sendMessageUsers = sendMessageData.length
    ? sendMessageData
    : selectedRow
  const selectedUserOptions = sendMessageUsers.map(
    ({ original }: { original: KASReportData }) => ({
      label: original.displayName || original.userId,
      value: original.playerId
    })
  )

  const linkOptions = keys.map((key, i) => ({
    key: `word-problems/${level}/${i + 1}`,
    label: HIDABLE_SELECT_LABELS[key]
  }))
  const exportColumn = isSchool
    ? SCHOOL_HIDABLE_SELECT_LABELS
    : HIDABLE_SELECT_LABELS
  const fileName = isSchool ? `${login.schoolName}_KAS` : `${team?.name}_KAS`
  const items = useDropdownMenu({
    table,
    hidableSelectLabel: exportColumn,
    fileName,
    hasColumnChange: isSchool ? ['grade'] : [],
    setIsLoading: setIsExporting
  })
  return (
    <>
      {/* navigation */}
      <Flex vertical gap="20px" style={{ position: 'relative' }}>
        {/* Loader overlay when loading and popup not open */}
        <LoaderOverlay isLoading={isLoading || isExporting} />
        <AssessmentReportNavigation
          exportButton={
            <ExportMenu
              ariaLabel="Export report options"
              items={items}
              trigger={['click']}
            />
          }
          filters={
            <Flex gap={14}>
              <Flex flex="1 1 auto" vertical>
                <p className="dd-label fw-bold text-sm">Level</p>
                <LevelSelect
                  value={level}
                  onChange={(selectedLevel) => {
                    setLevel(selectedLevel)
                    setSearchQuery('')
                  }}
                  options={levelOptions}
                />
              </Flex>
            </Flex>
          }
          dropdownFilters={dropdownFilters}
          setDropDownFilters={setDropDownFilters}
          isSchool={isSchool}
        />
        <Flex className="tiles-container" wrap="wrap">
          <Flex className="left" vertical gap="20px" flex="1 1 560px">
            <FactFluency type="green" />
            <AssessmentReportSummaryDashboard
              dropdownFilters={dropdownFilters}
              isSchool={isSchool}
            />
          </Flex>
          <Flex className="right right-graph-box" flex="1 1 560px">
            <Flex vertical className="graph-tiles" justify="space-between">
              {renderTitle(isSchool)}
              <Flex vertical align="center" className="graph-tiles__graph">
                <div className="graph-container">
                  {summaryRecordData && (
                    <AssessmentKSCharts
                      backgroundColor="#83E2B8"
                      level={level}
                      chartData={summaryRecordData}
                    />
                  )}
                </div>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        {/* result */}
        <div className="main-result">
          {/* Updated toolbar design */}
          <Flex
            justify="space-between"
            align="center"
            className="main-result__toolbar"
            wrap
          >
            <h4 className="title">
              {isSchool ? 'School Teams' : 'Team Players'}
            </h4>

            <Flex gap="10px" justify="space-between" wrap>
              <div className="report-search">
                <SearchInput value={searchQuery} onChange={setSearchQuery} />
              </div>
              {!isSchool && (
                <Button
                  intent="tertiary"
                  size="small"
                  disabled={isDisabled}
                  onClick={() => setIsModalOpen(true)}
                  className="print-hidden"
                >
                  SEND MESSAGE
                </Button>
              )}
            </Flex>
          </Flex>
          <Table />
        </div>
        {!isModalOpen && (
          <SelectedRowPopUp
            selectedRow={selectedRow}
            resetRowSelection={table.resetRowSelection}
            handleSendMessage={() => setIsModalOpen(true)}
          />
        )}
        <SendMessage
          level={levelOptions[level - 1]?.label}
          linkOptions={linkOptions}
          isModalOpen={isModalOpen}
          closeModal={(isSuccess) => {
            setIsModalOpen(false)
            setSendMessageData([])
            if (isSuccess) {
              table.resetRowSelection()
            }
          }}
          selectedUserOptions={selectedUserOptions}
          recipientEntityId={team?.id}
          reportType="Know and Show"
        />
      </Flex>
    </>
  )
}

export default AssessmentReportKnowAndShow
